import React, { useState, useEffect, useCallback, useContext } from 'react';
import CRMDirectory from '../../organisms/CRMDirectory/view';
import { getThirds, inactivateThird, reactivateThird } from '../../../usecases/thirds-usecase';
import { roleContext } from '../../../context/role/context';
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import EditThird from '../../organisms/editThird/view';
import ButtonAction from '../../atoms/buttonAction/view';
import { BsTrash, BsCheck } from 'react-icons/bs';
import './style.css';

/**
 * TercerosCRM - Modern CRM page for managing thirds.
 */
const TercerosCRM = () => {
    const { profile } = useContext(roleContext);

    // State
    const [data, setData] = useState({
        data: [],
        page: 1,
        page_size: 10,
        total: 0
    });
    const [filters, setFilters] = useState({
        search: '',
        type: 'all'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [selectedThird, setSelectedThird] = useState(null);

    // Fetch logic
    const fetchData = useCallback(async (page, limit, currentFilters) => {
        setIsLoading(true);
        try {
            const searchParams = [];
            if (currentFilters.search) searchParams.push({ label: 'query', value: currentFilters.search });
            if (currentFilters.type !== 'all' && currentFilters.type !== 'inactive') {
                searchParams.push({ label: 'type', value: currentFilters.type.toLowerCase() });
            }
            // If tab is inactive, query status inactive
            if (currentFilters.type === 'inactive') {
                searchParams.push({ label: 'status', value: 'inactive' });
            } else {
                searchParams.push({ label: 'status', value: 'all' });
            }

            const result = await getThirds(page, limit, searchParams);

            setData(prev => ({
                ...result,
                data: page === 1 ? (result?.data || []) : [...prev.data, ...(result?.data || [])],
                page,
                page_size: limit,
                total: result?.total || 0
            }));
        } catch (error) {
            console.error("Error fetching CRM data:", error);
            dispatchCustomEvent("popup", { message: "Error al cargar la lista de terceros", type: "error" });
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial load and filter change
    useEffect(() => {
        fetchData(1, data.page_size || 10, filters);
    }, [filters, fetchData]);

    // Listen for updates
    useEffect(() => {
        const cleanup = listener('update-thirds', () => fetchData(1, data.page_size || 10, filters));
        return () => { if (cleanup) cleanup(); };
    }, [fetchData, data.page_size, filters]);

    // Handlers
    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handlePageChange = (newPage) => {
        fetchData(newPage, data.page_size, filters);
    };

    const triggerInactivate = (third) => {
        setSelectedThird(third);
        dispatchCustomEvent("btn-notification-confirm-crm-inactivate", third);
    };

    const confirmInactivate = async () => {
        if (!selectedThird) return;
        setIsLoading(true);
        try {
            await inactivateThird(selectedThird.id);
            dispatchCustomEvent("popup", { message: "Tercero inactivado con éxito", type: "success" });
            await fetchData(1, data.page_size, filters);
        } catch (error) {
            dispatchCustomEvent("popup", { message: "Error al inactivar tercero", type: "error" });
        } finally {
            setIsLoading(false);
            setSelectedThird(null);
            dispatchCustomEvent('loading-btn', {});
        }
    };

    const triggerReactivate = (third) => {
        setSelectedThird(third);
        dispatchCustomEvent("btn-notification-confirm-crm-reactivate", third);
    };

    const confirmReactivate = async () => {
        if (!selectedThird) return;
        setIsLoading(true);
        try {
            await reactivateThird(selectedThird.id);
            dispatchCustomEvent("popup", { message: "Tercero reactivado con éxito", type: "success" });
            await fetchData(1, data.page_size, filters);
        } catch (error) {
            dispatchCustomEvent("popup", { message: "Error al reactivar tercero", type: "error" });
        } finally {
            setIsLoading(false);
            setSelectedThird(null);
            dispatchCustomEvent('loading-btn', {});
        }
    };

    // Render Actions Column
    const renderActions = (person) => (
        <>
            <EditThird data={person} />
            {person.active ? (
                <ButtonAction
                    btnKey={`inactivate-${person.id}`}
                    title={<BsTrash />}
                    onClick={() => triggerInactivate(person)}
                />
            ) : (
                <ButtonAction
                    btnKey={`reactivate-${person.id}`}
                    title={<BsCheck />}
                    onClick={() => triggerReactivate(person)}
                />
            )}
        </>
    );

    return (
        <div style={{ padding: '24px', height: '100%', boxSizing: 'border-box' }}>

            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-dark)', marginBottom: '4px' }}>
                    Directorio de Terceros
                </h1>
                <p style={{ color: 'var(--color-texto)', margin: 0, fontSize: '0.9rem' }}>
                    Gestiona clientes, proveedores y otros contactos comerciales de manera ágil.
                </p>
            </div>

            <CRMDirectory
                data={data.data}
                total={data.total}
                page={data.page}
                pageSize={data.page_size}
                filters={filters}
                onFilterChange={handleFiltersChange}
                onPageChange={handlePageChange}
                renderActions={renderActions}
                isLoading={isLoading}
            />

            <ConfirmPopUp
                eventKey="crm-inactivate"
                msg="¿Estás seguro de inactivar este tercero? Ya no aparecerá como opción activa en facturación ni compras."
                onAcept={confirmInactivate}
            />

            <ConfirmPopUp
                eventKey="crm-reactivate"
                msg="¿Deseas reactivar a este tercero para que vuelva a estar disponible en tus operaciones?"
                onAcept={confirmReactivate}
            />
        </div>
    );
};

export default TercerosCRM;
